import { useEffect, useState } from 'react';
import { Heart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CatalogItem } from '../../api/CataloguesAPI';
import noImage from '../../assets/images/null.png';
import { formatCatalogPrice } from '../../shared/catalogLookbook';
import PublicCatalogMedia, { CATALOG_MEDIA_SIZES } from './PublicCatalogMedia';

type PublicCatalogLookCardProps = {
  item: CatalogItem;
  saved: boolean;
  onOpen: () => void;
  onToggleSave: () => void;
};

const PublicCatalogLookCard = ({ item, saved, onOpen, onToggleSave }: PublicCatalogLookCardProps) => {
  const { t } = useTranslation();
  const media = item.media && item.media.length > 0 ? item.media : [noImage];
  const [activeIndex, setActiveIndex] = useState(0);
  const [hovering, setHovering] = useState(false);
  const current = media[activeIndex] || noImage;
  const price = formatCatalogPrice(item.price, item.currency);

  useEffect(() => {
    if (!hovering || media.length < 2) {
      return;
    }
    const timer = window.setInterval(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % media.length);
    }, 1200);
    return () => window.clearInterval(timer);
  }, [hovering, media.length]);

  return (
    <div
      className="group relative w-full overflow-hidden rounded-2xl bg-card text-left shadow-md transition duration-500 hover:-translate-y-1 hover:shadow-2xl"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => {
        setHovering(false);
        setActiveIndex(0);
      }}
    >
      <button type="button" onClick={onOpen} aria-label={item.name} className="block w-full text-left">
        <div className="relative aspect-[3/4] overflow-hidden bg-as-pink-100">
          <PublicCatalogMedia
            src={current}
            alt={item.name}
            className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
            sizes={CATALOG_MEDIA_SIZES.grid}
            loading="lazy"
          />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-4 pt-16">
            <h3 className="text-lg font-light tracking-wide text-white">{item.name}</h3>
            {price && <p className="mt-1 text-sm text-white/80">{price}</p>}
          </div>
          {media.length > 1 && (
            <span className="absolute bottom-3 right-3 rounded-full bg-black/55 px-2 py-0.5 text-[11px] font-medium text-white">
              {t('marketplace.catalogues.lookbook.photos', { count: media.length })}
            </span>
          )}
        </div>
      </button>
      <button
        type="button"
        onClick={onToggleSave}
        aria-label={saved ? t('marketplace.catalogues.lookbook.saved')! : t('marketplace.catalogues.lookbook.save')!}
        className={`absolute right-3 top-3 lookbook-overlay-chip p-2 ${
          saved ? 'bg-as-pink-300 text-white border-as-pink-300' : ''
        }`}
      >
        <Heart className={`h-4 w-4 ${saved ? 'fill-current' : ''}`} />
      </button>
    </div>
  );
};

export default PublicCatalogLookCard;
